import { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { usePathname } from 'expo-router'
import NativeQRCode from 'react-native-qrcode-svg'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { CRYPTO_ADDRESS_QUERY, GET_EXCHANGE_RATE_CONFIG } from '@/constants/graphql/queries'
import { marketPresentation } from '@/config/marketPresentation'
import { copyToClipboard } from '@/utils/copy'
import { toCurrency } from '@/utils/numbers'
import { Banner, useToast } from '@/components/base'
import CustomerServiceButton from '@/components/CustomerServiceButton'
import { Copy, Spinner, USDT } from '@/components/icon'
import { Stack, TabScreenContent } from '@/components/layout'
import NotificationButton from '@/components/NotificationButton'
import ScreenHeader from '@/components/ScreenHeader'

const screenOptions = { header: () => <Header /> }

export default function ConvertScreen() {
  const { t } = useTranslation()
  const toast = useToast()
  const [address, setAddress] = useState<string>('−')
  const [exchangeRate, setExchangeRate] = useState<string>('−.−−')
  const pathname = usePathname()

  useQuery(CRYPTO_ADDRESS_QUERY, {
    onCompleted: (data) => {
      const newAddress = data.cryptoAddress[0].address
      setAddress(newAddress)
    },
    onError: (error) => {
      console.error('error:', error)
      if (pathname === '/landing') {
        return
      }
      toast.show({ variant: 'warning', description: t('convert:addressLoadFailed') })
    }
  })

  useQuery(GET_EXCHANGE_RATE_CONFIG, {
    onCompleted: (data) => {
      const rate = toCurrency(data.getExchangeRateConfig.data.userSetting.tether)

      setExchangeRate(rate)
    },
    onError: (error) => {
      console.error('error:', error)
      if (pathname === '/landing') {
        return
      }
      toast.show({ variant: 'warning', description: t('convert:rateLoadFailed') })
    }
  })

  function handleCopy(text: string) {
    copyToClipboard(text)
    toast.show({ variant: 'success', description: t('convert:copied') })
  }

  return (
    <>
      <Stack.Screen options={screenOptions} />

      <TabScreenContent>
        <ScrollView className={'bg-background-secondary'}>
          <View className={'border-b border-border-10 bg-background-primary px-4 pb-4 '}>
            <Banner mood={'info'}>
              {t('convert:conversionDescription', { token: marketPresentation.platformToken.displayName })}
            </Banner>
          </View>
          <View className={'mx-4 mt-4 items-center rounded-lg border border-border-10 bg-background-primary px-4 py-6'}>
            <View className={'flex-row items-center gap-2'}>
              <USDT className={'size-6'} />
              <Text className={'text-base font-medium text-text-primary'}>{t('convert:usdtTrc20')}</Text>
            </View>
            <View className={'h-4'} />
            <View className={'w-full max-w-[256px]'}>
              <View className={'border border-border-10 p-4'}>
                {address !== '−' ? (
                  <View className={'items-center'}>
                    <NativeQRCode value={address} size={224} />
                  </View>
                ) : (
                  <View className={'aspect-square items-center justify-center'}>
                    <View className={'animate-spin'}>
                      <Spinner className={'size-6 text-primary'} />
                    </View>
                  </View>
                )}
              </View>
            </View>
            <View className={'h-3'} />
            <View className={'w-full'}>
              <Pressable
                className={'mx-auto max-w-fit flex-row gap-1 rounded-lg bg-primary-subtle px-2 py-1.5'}
                onPress={() => handleCopy(address)}
              >
                <Text className={'overflow-hidden text-ellipsis text-xs font-medium text-primary'}>{address}</Text>
                {address !== '−' && <Copy className={'size-4 min-w-4 text-primary'} />}
              </Pressable>
            </View>
            <View className={'h-4'} />
            {/* divider */}
            <View className={'h-px w-full bg-border-10'} />
            <View className={'h-4'} />
            <View className={'w-full flex-row justify-between'}>
              <Text className={'text-sm text-text-secondary'}>{t('convert:rate')}</Text>
              <Text
                className={'font-roboto-medium text-sm font-medium text-text-primary'}
              >{t('convert:rateFromUsdt', { amount: exchangeRate, token: marketPresentation.platformToken.displayName })}</Text>
            </View>
          </View>
          <HintSection />
        </ScrollView>
      </TabScreenContent>
    </>
  )
}

function Header() {
  const { t } = useTranslation()
  const { top } = useSafeAreaInsets()

  return (
    <View className={'bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={<CustomerServiceButton />}
        centerElement={<Text className={'text-base font-medium text-text-primary'}>{t('convert:title')}</Text>}
        rightElement={<NotificationButton />}
      />
    </View>
  )
}

function HintSection() {
  const { t } = useTranslation()
  return (
    <>
      <View className={'bg-background-secondary px-4 pb-2 pt-5'}>
        <Text className={'text-sm text-text-primary'}>{t('convert:hintTitle')}</Text>
      </View>
      <View className={'flex-row gap-[6px] pl-6 pr-8'}>
        <Text className={'text-sm text-text-secondary'}>{'1.'}</Text>
        <Text className={'text-sm text-text-secondary'}>
          {t('convert:addressOnlySupports')} <Text className={'text-sm text-text-danger'}>{t('convert:usdtTrc20Network')}</Text>{' '}
          {t('convert:addressOnlySupportsSuffix')}
        </Text>
      </View>
      <View className={'mb-4 flex-row gap-1 pl-6 pr-8'}>
        <Text className={'text-sm text-text-secondary'}>{'2.'}</Text>
        <Text className={'text-sm text-text-secondary'}>
          {t('convert:confirmationHint')}
        </Text>
      </View>
    </>
  )
}
